import { SBBox, SBGrid, SBLoading } from "modules/Commons";
import { SBNewTable } from "modules/Commons/SBDataDisplays/SBTable/SBNewTable";
import SBEmptyState from "modules/Commons/SBLayouts/SBEmptyState";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import { useGlobalInventoryContext } from "modules/Inventory/components/SBGlobalInventoryInfoModal";
import { useOptimizationRequester } from "modules/Optimizations/hooks/useOptimizationRequester";
import TransferenceConfirmation from "modules/Transferences/components/TransferenceConfirmation";
import TransferenceEmptyState from "modules/Transferences/components/TransferenceEmptyState";
import TransferenceSummarization from "modules/Transferences/components/TransferenceSummarization";
import { useTransferencePlanHeaders } from "modules/Transferences/models/tableHeaders";
import { TransferencePlan } from "modules/Transferences/models/transferencePlan";
import { useTransferenceOrderRequester } from "modules/TransferenceOrder/hooks/useTransferenceOrderRequester";
import { ITransferenceOrder } from "domain/Inventory/Models/ITransferenceOrder";
import { IMovementPlanSummary } from "domain/Inventory/Models/IMovementPlanSummary";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SBComposedFilter from "views/Commons/SBComposedFilter";
import { IComposedFilter } from "domain/Utils";
import { useNotificationsRequester } from "modules/Notifications/hooks/useNotificationsRequester";
import { useSystemFilterContext } from "contexts/SystemFilterContext";
import { useDateStorageCache } from "hooks/useDateStorageCache";
import SBContextDateInfo from "views/Commons/SBContextDateInfo";
import { DateTime } from "luxon";
import { useMixpanelContext } from "contexts/MixpanelContext";
import { SBTranslate } from "i18n";

const getID = (c: TransferencePlan) => c.uuid;

export default () => {
	const handleClick = (id: number) => {
		openMenu(id);
	};
	const tableRef = useRef<any>();
	const { t } = useTranslation();
	const { headers } = useTransferencePlanHeaders(handleClick);
	const { context, lastDatabaseUpdate, filtersContext, setFiltersContextState, notCurrentContext } = useSystemFilterContext();
	const { getDateItem, setDateItem } = useDateStorageCache();
	const { trackHistoryChange, trackExport } = useMixpanelContext();
	const notificationsRequester = useNotificationsRequester();
	const optimizationRequester = useOptimizationRequester();
	const transferenceOrderRequester = useTransferenceOrderRequester();
	const { error, success } = useSnackbar();

	const [availableDates, setAvailableDates] = useState<string[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingExport, setLoadingExport] = useState(false);
	const [dialogStatus, setDialogStatus] = useState(false);
	const [orderLoading, setOrderLoading] = useState(false);

	const { openMenu, isOpen } = useContext(useGlobalInventoryContext);

	const [transferencePlans, setTransferencePlans] = useState(
		[] as TransferencePlan[]
	);
	const [totalCount, setTotalCount] = useState(0);
	const [summaryBase, setSummaryBase] = useState<IMovementPlanSummary | undefined>();
	const [skip, setSkip] = useState(0);
	const take = 100;
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const hasMore = skip + take < totalCount;

	const hasAnyPlans = useMemo(
		() => transferencePlans?.length > 0,
		[transferencePlans]
	);

	const fetchPlans = async () => {
		setLoading(true);
		setSkip(0);
		
		optimizationRequester.getTransferencePlansCount(filtersContext?.items ?? [])
			.then((c) => {
				if (c) {
					setTotalCount(c.totalCount);
					setSummaryBase(c);
				} else {
					setTotalCount(0);
					setSummaryBase(undefined);
				}
			});

		optimizationRequester.getTransferencePlans(filtersContext?.items ?? [], 0, take)
			.then((d) => setTransferencePlans(d))
			.finally(() => setLoading(false));
	};

	const fetchMorePlans = async () => {
		if (isFetchingMore || !hasMore) return;
		setIsFetchingMore(true);
		const newSkip = skip + take;
		setSkip(newSkip);
		optimizationRequester.getTransferencePlans(filtersContext?.items ?? [], newSkip, take)
			.then((d) => setTransferencePlans([...transferencePlans, ...d]))
			.finally(() => setIsFetchingMore(false));
	};

	const handleScroll = (event: any) => {
		const target = event.target;
		if (target.scrollHeight - target.scrollTop <= target.clientHeight + 100) {
			fetchMorePlans();
		}
	};

	const getAvailableDates = async () => {
		const result = await notificationsRequester.homeStatisticsDates();
		if (result) setAvailableDates(result)
	};

	const handleOnApply = async (obj: IComposedFilter) => {
		obj.picker?.date && setDateItem(obj.picker.date.toFormat("yyyy-MM-dd"))

		setLoading(true);
		setSkip(0);
		if (obj.filter)
			setFiltersContextState(obj.filter);
		else
			setFiltersContextState([]);

		const filter = obj.filter ?? [];
		trackHistoryChange({ date: getDateItem() });

		optimizationRequester.getTransferencePlansCount(filter)
			.then((c) => {
				if (c) {
					setTotalCount(c.totalCount);
					setSummaryBase(c);
				} else {
					setTotalCount(0);
					setSummaryBase(undefined);
				}
			});

		optimizationRequester.getTransferencePlans(filter, 0, take)
			.then((d) => setTransferencePlans(d))
			.finally(() => setLoading(false));
	};

	const handleExport = () => {
		setLoadingExport(true);
		trackExport({ date: getDateItem() });
		tableRef.current.downloadCSV({});
		setLoadingExport(false);
	};

	const sendTransferenceToOrders = () => {
		const selectedIds: string[] = tableRef.current.selecteds();

		if (selectedIds.length === 0) {
			error(SBTranslate("noRowsSelectedForOrder"));
			return;
		}

		const eligiblePlans = transferencePlans.flatMap((group) =>
			(group.plans ?? []).filter(
				(plan) => selectedIds.indexOf(getID(plan)) >= 0
			)
		);

		const orders: Partial<ITransferenceOrder>[] = eligiblePlans.map((plan) => {
			const group = transferencePlans.find((g) =>
				(g.plans ?? []).some((p) => getID(p) === getID(plan))
			);
			return {
				guid: plan.uuid,
				productClientID: plan.product?.clientID ?? group?.product?.clientID,
				sourceStoreClientID: plan.source?.clientID ?? group?.inventory?.store?.clientID,
				destinationStoreClientID: plan.store?.clientID,
				created: plan.cycle?.request,
				expected: plan.cycle?.delivery,
				supplyBrainRecommendation: plan.amount,
				quantity: plan.amount,
			} as Partial<ITransferenceOrder>;
		});

		setOrderLoading(true);
		transferenceOrderRequester.createOrders(orders)
			.then((count: number) => {
				const failed = orders.length - count;
				if (failed === 0) {
					success(SBTranslate("ordersCreatedSuccess"));
				} else {
					error(SBTranslate("ordersPartialError", { count: failed }));
				}
				setDialogStatus(false);
				fetchPlans();
			})
			.catch(() => {
				error(SBTranslate("ordersCreatedError"));
				setDialogStatus(false);
			})
			.finally(() => setOrderLoading(false));
	};

	useEffect(() => {
		if (context)
			fetchPlans();
	}, [context]);

	useEffect(() => {
		getAvailableDates();
	}, []);

	return (
		<SBBox height={1} pl={11}>
			<SBBox display="flex" justifyContent="end">
				<SBComposedFilter.Base onApply={handleOnApply}>
					<SBComposedFilter.Picker date={getDateItem() && DateTime.fromISO(getDateItem())} range={false} showConfigOptions={false} showDateGranularityOptions={false} availableDates={availableDates} />
					<SBComposedFilter.Filter />
					<SBComposedFilter.Applicator />
					<SBComposedFilter.Actions
						disabled={!hasAnyPlans}
						loading={loadingExport}
						actions={[
							{
								title: "createOrders",
								icon: { name: "shopping-cart" },
								onClick: () => setDialogStatus(true),
							},
							{
								title: "exportCSV",
								icon: { name: "download" },
								onClick: handleExport,
							},
						]}
					/>
				</SBComposedFilter.Base>
			</SBBox>

			{notCurrentContext() ? <SBGrid container>
				<SBContextDateInfo context={DateTime.fromISO(getDateItem())} />
			</SBGrid> : <></>}

			<SBLoading loading={loading}>
				<SBEmptyState
					show={hasAnyPlans}
					emptyState={<TransferenceEmptyState />}
				>
					<TransferenceSummarization
						transferencePlans={transferencePlans}
						summaryBase={summaryBase}
					/>

					<SBBox
						width={isOpen ? "50vw" : "100%"}
						style={{
							transition: "all 0.25s ease",
							marginTop: "1rem",
						}}
					>
						<SBNewTable
							ref={tableRef}
							virtualize
							hasSelection
							collapseFieldName={"plans"}
							title={t("suggestedTransferences") as string}
							defaultColumns={headers}
							startRows={transferencePlans}
							onClick={(cell: any) => {
								if (cell.row.getCanExpand()) {
									cell.row.toggleExpanded();
								}
							}}
							onDataUpdated={(
								items: TransferencePlan[]
							) => {
								setTransferencePlans(items);
							}}
							totalCount={totalCount}
							onScroll={handleScroll}
						/>
					</SBBox>
				</SBEmptyState>
			</SBLoading>
			<TransferenceConfirmation
				open={dialogStatus}
				loading={orderLoading}
				onConfirm={sendTransferenceToOrders}
				onClose={() => setDialogStatus(false)}
				onCancel={() => setDialogStatus(false)}
			/>
		</SBBox>
	);
};
